/**
 *
 * @requires underscorejs
 */
window.DownloadableFonts = class {

	constructor (opts) {

		this._opts = _.clone(opts);
		this.weights = this._opts.weights;
		this.fonts = [];

	}

	/** @protected */
	_capitalize (str) {

		return str.charAt(0).toUpperCase() + str.slice(1);

	}

	/** @protected */
	_decapitalize (str) {

		return str.charAt(0).toLowerCase() + str.slice(1);

	}

	/**
	 * Convert weight to its name. 400 -> 'regular', 900 -> 'black' and so on
	 * @param {Integer} weight
	 * @return {String}
	 */
	_weightToName (weight) {

		return _.findKey(this.weights, (w) => w == weight);

	}

	/**
	 * Returns path to font file
	 * @protected
	 * @returns {String}
	 */
	_getFontFile (family, weight, style) {

		return `${this._opts.fontsPath + family.replace(/ /g, '')}-${this._capitalize(
			this.subFamily(weight, style)
		)}.woff`;

	}

	/**
	 * Returns true if examinated font has exactly same family, weight and style
	 * @param {Object} font
	 * @param {String} family
	 * @param {Integer} weight
	 * @param {String} style
	 * @return {Boolean}
	 */
	checkFont (font, family, weight, style) {

		const fFamily = (
			(font.tables.name.preferredFamily && font.tables.name.preferredFamily.en)
			|| font.tables.name.fontFamily.en
		).trim();
		const fWeight = parseInt(font.tables.os2.usWeightClass, 10);
		const fStyle = font.tables.name.fontSubfamily.en.toLowerCase().search('italic') >= 0 ? 'italic' : 'normal';

		return fFamily == family && fWeight == weight && fStyle == style;

	}

	/** */
	getFontsPreviewsList () {

		const result = [];

		// for each font
		this._opts.list.forEach((font, i) => {

			// for every weight
			_.forEach(this._opts.weights, (weight) => {

				// get array of available styles for given weight
				this.getStyles(i, weight).forEach((style) => {

					result.push({
						i,
						fontFamily: font.name,
						subFamily: this._capitalize(this.subFamily(weight, style)),
						weight,
						style
					});

				});

			});

		});

		return result;

	}

	/**
	 * Returns subFamily, something like 'boldItalic', 'extraBoldItalic', 'regularItalic' and so on
	 * @return {String}
	 */
	subFamily (weight, style) {

		return this._weightToName(weight) + (style == 'normal' ? '' : this._capitalize(style));

	}

	hasSubFamily (i, weight, style = 'normal') {

		return this._opts.list[i].style & this._opts.flags[this.subFamily(weight, style)];

	}

	/**
	 * Returns array of available styles for given font index and weight
	 * @param {Integer} i
	 * @param {Integer} weight
	 * @return {Array}
	 */
	getStyles (i, weight) {

		const styles = [];
		this._opts.list[i].style & this._opts.flags[this.subFamily(weight, 'normal')] && styles.push('normal');
		this._opts.list[i].style & this._opts.flags[this.subFamily(weight, 'italic')] && styles.push('italic');

		return styles;

	}

	/**
	 * Return information about font, see qtee.customDesign.settings.fonts
	 * @return {Object}
	 */
	getInfo (i) {

		return this._opts.list[i];

	}

	getFont (family, weight, style) {

		return _.find(this.fonts, (f) => this.checkFont(f, family, weight, style));

	}

	/**
	 * @return {Promise} resolved when font file is downloaded and added to this.fonts
	 */
	addFont (family, weight, style) {

		return new Promise((resolve, reject) => {

			// check if we already have the font
			const font = this.getFont(family, weight, style);

			if (font) {

				// font was already downloaded
				resolve(font);

			} else {

				const fontFile = this._getFontFile(family, weight, style);
				opentype.load(fontFile, (err, f) => {

					if (err) {

						reject(`The font file '${fontFile}' has not been downloaded`);

					} else {

						if (this.checkFont(f, family, weight, style)) {

							this.fonts.push(f);
							resolve(f);

						} else {

							reject(
								`There is no such font (${family}, ${weight}, ${style}) in the font file "${fontFile}"`
							);

						}

					}

				});

			}

		});

	}

	/**
	 * 	Parse Scene7 font name to font family and weight
	 * Examples:
	 * 'Abcd Sans Extrabold' -> { family: 'Abcd Sans', weight: 800 }
	 * 'New Awesome Font' -> { family: 'New Awesome Font', weight: 400 }
	 */
	parseFontName (fontName) {

		const splittedFontName = fontName.split(' ');
		const suggestedWeight = this._decapitalize(splittedFontName[splittedFontName.length - 1]);
		const suggestedFontFamily = splittedFontName.slice(0, splittedFontName.length - 1).join(' ');
		let family;
		let weight;

		if (this.weights[suggestedWeight] && _.findWhere(this._opts.list, { name: suggestedFontFamily })) {

			family = suggestedFontFamily;
			weight = this.weights[suggestedWeight];

		} else {

			family = fontName;
			weight = this.weights.regular;

		}

		return { family, weight };

	}

	/**
	 * Convert font family and weight to Scene7 font name
	 * Examples:
	 * { family: 'Abcd Sans', weight: 800 } -> 'Abcd Sans Extrabold'
	 * { family: 'New Awesome Font', weight: 400 } -> 'New Awesome Font'
	 */
	getFontName (family, weight) {

		const fw = weight == this.weights.regular ? '' : ` ${this._capitalize(this._weightToName(weight))}`;
		return family + fw;

	}

};
